import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import { qk } from "@/api/queryKeys";
import { t } from "@/i18n/ko";
import { productName } from "@/i18n/codes";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import type { PurchaseDoneState } from "../usePurchase";
import type { StoreState } from "../StorePage";
import { isTossCancel, takeCheckoutProduct } from "./webCheckout";

/**
 * 토스 successUrl (상점·결제 설계 결정 10): ?paymentKey&orderId&amount 를 받아 POST /purchases/web/confirm → granted|already_granted 면 /store/done,
 * held 면 안내 뒤 상점, 실패(402·404·409)는 토스트 뒤 상점. 금액은 서버가 주문과 대조하므로 클라는 그대로 넘긴다.
 */
export function WebSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const paymentKey = params.get("paymentKey");
    const orderId = params.get("orderId");
    const amount = Number(params.get("amount"));
    if (!paymentKey || !orderId || !Number.isFinite(amount)) {
      showSnackbar(t("store.purchase.failed"));
      navigate("/store", { replace: true });
      return;
    }
    void (async () => {
      try {
        const res = await call(api.POST("/purchases/web/confirm", { params: { header: xClient() }, body: { paymentKey, orderId, amount } }));
        if (res.status === "held") {
          showSnackbar(t("store.purchase.held"));
          navigate("/store", { replace: true });
          return;
        }
        useAuthStore.getState().setBalance(res.balance);
        void qc.invalidateQueries({ queryKey: qk.bricks });
        void qc.invalidateQueries({ queryKey: qk.me });
        void qc.invalidateQueries({ queryKey: qk.products });
        void qc.invalidateQueries({ queryKey: ["transactions"] });
        const state: PurchaseDoneState = { purchase: res, name: productName({ bricks: res.purchase.bricks }) };
        navigate("/store/done", { replace: true, state });
      } catch (e) {
        const code = isApiError(e) ? e.code : "UNKNOWN";
        showSnackbar(code === "NETWORK" ? t("error.network") : t("store.web.confirmFailed"));
        navigate("/store", { replace: true });
      }
    })();
  }, [params, navigate, qc, showSnackbar]);
  return (
    <main className="page" data-testid="store-web-success">
      <div className="page-placeholder" role="status">
        {t("store.web.confirming")}
      </div>
    </main>
  );
}

/** 토스 failUrl: ?code&message&orderId. 사용자 취소는 조용히, 그 외는 상점의 F06-03 다이얼로그(state.paymentFailed = 떠나기 전 상품). 어느 쪽이든 상점으로 */
export function WebFailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    const code = params.get("code");
    const productId = takeCheckoutProduct();
    if (code && !isTossCancel(code)) {
      const state: StoreState = { paymentFailed: productId };
      navigate("/store", { replace: true, state });
    } else {
      navigate("/store", { replace: true });
    }
  }, [params, navigate]);
  return null;
}
